import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { IntegrationTypes } from '@mm/state/model/ConnectionModel';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export const useFetchConnections = () => {
  const connectionsCRUDActions = useConnectionCRUDActions();
  const connectedPageId = useSelector(activeFacebookPageIdSelector);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const pagination = useSelector(
    (state: StoreState) => state.connections.pagination,
  );

  const [isFetching, setIsFetching] = useState(false);
  const fetchState = useRef<{
    busy: boolean;
    pageNumber: number;
    pageSize: number;
    type?: IntegrationTypes;
  }>({
    busy: false,
    pageNumber: !pagination?.isLastPage ? pagination.page : 0,
    pageSize: pagination.perPage,
    type: null,
  });

  const fetchMore = useCallback(async () => {
    if (!fetchState.current.busy && fetchState.current.pageNumber) {
      fetchState.current.busy = true;
      setIsFetching(true);
      const res = await connectionsCRUDActions.retrieveAll(connectedPageId, {
        pageNumber: fetchState.current.pageNumber + 1,
        pageSize: fetchState.current.pageSize,
        type: fetchState.current.type,
      });

      setIsFetching(false);
      fetchState.current.busy = false;
      fetchState.current.pageNumber =
        res?.pagination.isLastPage === false ? res.pagination.page : 0;
      return res;
    }
  }, [connectionsCRUDActions, connectedPageId]);

  const fetch = useCallback(
    (
      pageNumber: number,
      pageSize: number,
      type?: IntegrationTypes,
      query = '',
    ) =>
      (async () => {
        if (!fetchState.current.busy) {
          fetchState.current.busy = true;
          setIsFetching(true);
          const res = await connectionsCRUDActions.retrieveAll(
            connectedPageId,
            {
              pageNumber,
              pageSize,
              type,
              query,
            },
          );
          setIsFetching(false);
          fetchState.current = {
            busy: false,
            pageSize,
            pageNumber:
              res?.pagination.isLastPage === false ? res.pagination.page : 0,
            type,
          };
          return res;
        }
      })(),
    [connectedPageId, connectionsCRUDActions],
  );

  useEffect(() => {
    fetchMore();
  }, [activeChatbotId, fetchMore]);

  return { fetch, fetchMore, isFetching, pagination };
};
